import { createContext, use, useCallback, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ErreurApi, type SessionOuverte } from "./api/client";

/** Ce qu'une connexion demande encore : rien, ou le code du second facteur. */
export type SuiteDeConnexion = { preuve: string } | null;

interface Contexte {
  session: SessionOuverte | null;
  chargement: boolean;
  /** Vrai si la permission est accordée sur l'ensemble du tenant. */
  peut: (permission: string) => boolean;
  /** Premier temps : rend la preuve à échanger contre le code, si le second facteur est actif. */
  connecter: (courriel: string, motDePasse: string) => Promise<SuiteDeConnexion>;
  /** Second temps de la connexion (EF-0103). */
  confirmer: (preuve: string, code: string) => Promise<void>;
  /** Remplace la session — après l'activation du second facteur. */
  actualiser: (session: SessionOuverte) => void;
  deconnecter: () => Promise<void>;
  changerDeTenant: (tenant: string) => Promise<void>;
}

const ContexteSession = createContext<Contexte | null>(null);

export function FournisseurSession({ children }: { children: ReactNode }) {
  const cache = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        return await api.session();
      } catch (erreur) {
        // 401 n'est pas une panne : c'est l'absence de session. La distinguer
        // évite d'afficher une erreur là où il faut simplement se connecter.
        if (erreur instanceof ErreurApi && erreur.statut === 401) return null;
        throw erreur;
      }
    },
    retry: false,
    staleTime: 0,
  });

  const connexion = useMutation({
    mutationFn: ({ courriel, motDePasse }: { courriel: string; motDePasse: string }) =>
      api.ouvrirSession(courriel, motDePasse),
    onSuccess: (reponse) => {
      if (!("secondFacteurRequis" in reponse)) cache.setQueryData(["session"], reponse);
    },
  });

  const confirmation = useMutation({
    mutationFn: ({ preuve, code }: { preuve: string; code: string }) => api.ouvrirAvecLeCode(preuve, code),
    onSuccess: (session) => cache.setQueryData(["session"], session),
  });

  const bascule = useMutation({
    mutationFn: (tenant: string) => api.changerDeTenant(tenant),
    onSuccess: (session) => {
      cache.setQueryData(["session"], session);
      // Tout ce qui a été lu l'a été dans l'ancien tenant : rien n'en survit.
      cache.removeQueries({ predicate: (requete) => requete.queryKey[0] !== "session" });
    },
  });

  const connecter = useCallback(
    async (courriel: string, motDePasse: string): Promise<SuiteDeConnexion> => {
      const reponse = await connexion.mutateAsync({ courriel, motDePasse });
      return "secondFacteurRequis" in reponse ? { preuve: reponse.preuve } : null;
    },
    [connexion],
  );

  const confirmer = useCallback(
    async (preuve: string, code: string) => {
      await confirmation.mutateAsync({ preuve, code });
    },
    [confirmation],
  );

  const actualiser = useCallback((session: SessionOuverte) => cache.setQueryData(["session"], session), [cache]);

  const deconnecter = useCallback(async () => {
    await api.fermerSession();
    cache.clear();
    cache.setQueryData(["session"], null);
  }, [cache]);

  const changerDeTenant = useCallback(
    async (tenant: string) => {
      await bascule.mutateAsync(tenant);
    },
    [bascule],
  );

  const session = data ?? null;

  const valeur = useMemo<Contexte>(
    () => ({
      session,
      chargement: isPending,
      peut: (permission: string) => session?.permissions.includes(permission) ?? false,
      connecter,
      confirmer,
      actualiser,
      deconnecter,
      changerDeTenant,
    }),
    [session, isPending, connecter, confirmer, actualiser, deconnecter, changerDeTenant],
  );

  return <ContexteSession value={valeur}>{children}</ContexteSession>;
}

export function useSession(): Contexte {
  const contexte = use(ContexteSession);
  if (contexte == null) throw new Error("useSession employé hors de FournisseurSession.");
  return contexte;
}
